import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { useAppSelector } from '@/stores/hooks.ts'
import { c2bSelectors } from '@/stores/slices/c2b-slice.ts'
import { b2bSelectors } from '@/stores/slices/b2b-slice.ts'

import { B2BQr, C2BQr, RegisterB2BQr, RegisterC2BQrParams, RegisterStaticC2BQrParams, TableData } from '../types.ts'
import { omitRequestParams } from '../utils.ts'

import { apiService } from './root.ts'

const QR_ROOT = '/sbp-requests'

export const QR_ENDPOINTS = {
  list: QR_ROOT,
  item: (id: string) => `${QR_ROOT}/${id}`,
  registerB2B: `/registerb2b`,
  registerC2B: `/registerc2b`,
  registerC2BStatic: '/registerc2b/static',
  deleteStaticC2b: (id: string) => `/delete/${id}`,
}

export const useGetB2BQrs = () => {
  const { page, showCount } = useAppSelector(b2bSelectors.filters)

  const queryParams = omitRequestParams({
    page,
    showCount,
    type: 'B2B',
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: [`B2B_list`, queryParams],
    retry: 2,
    queryFn: () =>
      apiService.get<TableData<B2BQr>>(QR_ENDPOINTS.list, {
        queryParams,
      }),
  })
}

export const useGetC2BQrs = () => {
  const { page, showCount } = useAppSelector(c2bSelectors.filters)
  const qrcType = useAppSelector((state) => state.c2b.qrcType)

  const queryParams = omitRequestParams({
    page,
    showCount,
    qrcType,
    type: 'C2B',
  })

  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: [`C2B_list`, queryParams],
    retry: 2,
    queryFn: () =>
      apiService.get<TableData<C2BQr>>(QR_ENDPOINTS.list, {
        queryParams,
      }),
  })
}

export interface InvalidRequestResponse {
  state: 'REJECTED'
  errorMessage: string
  errorCode: number
}

export const useMutateRegisterB2B = () =>
  useMutation({
    mutationFn: (bodyParams: RegisterB2BQr) =>
      apiService.post<InvalidRequestResponse | B2BQr>(QR_ENDPOINTS.registerB2B, {
        bodyParams,
      }),
  })

export const useMutateRegisterC2B = () =>
  useMutation({
    mutationFn: (bodyParams: RegisterC2BQrParams | RegisterStaticC2BQrParams) =>
      apiService.post<InvalidRequestResponse | C2BQr>(QR_ENDPOINTS.registerC2B, { bodyParams }),
  })

export const useMutateDeleteC2B = (id: string | number) =>
  useMutation({
    mutationFn: () => apiService.delete<InvalidRequestResponse | C2BQr>(QR_ENDPOINTS.deleteStaticC2b(id.toString())),
  })
